import React, { useState, useRef, useEffect, useCallback } from 'react';
import * as Tone from 'tone';
import {
  DndContext,
  pointerWithin,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  Modifier,
} from '@dnd-kit/core';
import {
  useDraggable,
  useDroppable,
} from '@dnd-kit/core';
import { bachInvention779Level } from '../data/levels';
import { LevelConfig, Fragment, HintState } from '../data/levelSystem';
import NavigationHeader from './NavigationHeader';
import './BachPuzzle.css';

interface FragmentState {
  id: string;
  currentBeat: number | null; // null means not placed yet, 0-7 for beat positions
  pitchOffset: number; // semitones from original
  isActive: boolean;
}

interface BachPuzzleProps {
  level?: LevelConfig;
  onSolved: (solved: boolean) => void;
  onBackToLevels?: () => void;
  onNextLevel?: () => void;
}

// Custom light X-axis snapping modifier that aligns with drop zones
const createLightXAxisSnapModifier = (beatWidth: number, totalBeats: number): Modifier => {
  return ({ transform }) => {
    const rawX = transform.x;
    // Calculate which beat position this corresponds to
    const beatPosition = rawX / beatWidth;
    // Round to nearest beat (0 to totalBeats-1) - snapping only works within musical beats
    const nearestBeat = Math.max(0, Math.min(totalBeats - 1, Math.round(beatPosition)));
    const nearestGridX = nearestBeat * beatWidth;
    const snapDistance = Math.abs(rawX - nearestGridX);
    const snapThreshold = 75;
    
    // Only snap if we're within the musical timeline (beats 0 to totalBeats-1)
    if (beatPosition >= 0 && beatPosition <= totalBeats - 1 && snapDistance < snapThreshold) {
      const snapStrength = 1 - (snapDistance / snapThreshold);
      const snapForce = 0.8;
      const pullAmount = (nearestGridX - rawX) * snapStrength * snapForce;
      const newX = rawX + pullAmount;
      
      return {
        ...transform,
        x: newX,
      };
    }
    
    return transform;
  };
};

// Calculate maximum fragment duration across all fragments
const getMaxFragmentDuration = (fragments: Fragment[]) => {
  return Math.max(...fragments.map(fragment => {
    return fragment.beatDuration;
  }));
};


// Timeline Fragment Component (draggable fragments on timeline)
const TimelineFragment: React.FC<{
  fragment: Fragment;
  state: FragmentState;
  beatWidth: number; // Width of one beat in pixels
  isDragging?: boolean;
}> = ({ fragment, state, beatWidth, isDragging = false }) => {
  const { attributes, listeners, setNodeRef, transform } = useDraggable({
    id: fragment.id,
  });

  // Use the fragment's beat duration directly
  const fragmentDuration = fragment.beatDuration;
  const fragmentWidth = fragmentDuration * beatWidth; // Width in pixels

  const style = transform ? {
    transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`,
    width: `${fragmentWidth}px`,
    opacity: isDragging ? 0 : 1,
  } : {
    width: `${fragmentWidth}px`,
    opacity: isDragging ? 0 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...listeners}
      {...attributes}
      className="timeline-fragment"
      data-fragment={fragment.id}
    >
      <div className="fragment-header">
        <div className="fragment-label">
          {fragment.name}
        </div>
      </div>
      <div className="fragment-notes">
        {fragment.notes.slice(0, 6).map((note, i) => (
          <span key={i} className="note-indicator">
            {note.note === 'rest' ? '𝄽' : '♪'}
          </span>
        ))}
        {fragment.notes.length > 6 && <span className="more-notes">...</span>}
      </div>
      <div className="fragment-duration">
        {fragmentDuration.toFixed(1)} beats
      </div>
    </div>
  );
};

// Fragment Bank Component
const FragmentBank: React.FC<{
  fragments: Fragment[];
  fragmentStates: FragmentState[];
  beatWidth: number;
  activeId?: string | null;
}> = ({ fragments, fragmentStates, beatWidth, activeId }) => {
  const { isOver, setNodeRef } = useDroppable({
    id: 'fragment-bank',
  });

  const unplacedFragments = fragments.filter(fragment => {
    const state = fragmentStates.find(s => s.id === fragment.id);
    return state && state.currentBeat === null;
  });

  return (
    <div 
      ref={setNodeRef}
      className={`fragment-bank ${isOver ? 'drop-hover' : ''}`}
    >
      <h3>Available Fragments</h3>
      <div className="fragments-list">
        {unplacedFragments.map(fragment => {
          const state = fragmentStates.find(s => s.id === fragment.id);
          if (!state) return null;

          return (
            <TimelineFragment
              key={fragment.id}
              fragment={fragment}
              state={state}
              beatWidth={beatWidth}
              isDragging={activeId === fragment.id}
            />
          );
        })}
      </div>
    </div>
  );
};

// Timeline Fallback Drop Zone Component
const TimelineFallbackDropZone: React.FC = () => {
  const { setNodeRef } = useDroppable({
    id: 'timeline-fallback-drop',
  });

  return (
    <div 
      ref={setNodeRef}
      className="timeline-fallback-drop" 
      style={{ 
        position: 'absolute', 
        top: 0, 
        left: 0, 
        right: 0, 
        bottom: 0, 
        zIndex: 0 
      }} 
    />
  );
};

// Beat Drop Zone Component
const BeatDropZone: React.FC<{
  absoluteBeat: number;
  beatWidth: number;
}> = ({ absoluteBeat, beatWidth }) => {
  const { setNodeRef } = useDroppable({
    id: `beat-${absoluteBeat}`,
  });

  const leftPosition = absoluteBeat * beatWidth;

  return (
    <div
      ref={setNodeRef}
      className="beat-drop-zone"
      style={{
        position: 'absolute',
        left: `${leftPosition}px`,
        top: 0,
        width: `${beatWidth}px`,
        height: '100%',
        border: 'none',
        backgroundColor: 'transparent',
        zIndex: 1,
      }}
    />
  );
};

// Single Timeline Track Component (for all fragments)
const Timeline: React.FC<{
  fragments: Fragment[];
  fragmentStates: FragmentState[];
  trackWidth: number;
  beatWidth: number;
  totalVisualBeats: number;
  musicalBeats: number;
  dynamicBeatRange: { startBeat: number; endBeat: number };
  activeId?: string | null;
  currentPlaybackBeat?: number;
}> = ({ fragments, fragmentStates, trackWidth, beatWidth, totalVisualBeats, musicalBeats, dynamicBeatRange, activeId, currentPlaybackBeat = -1 }) => {
  return (
    <div
      className="timeline-track"
      style={{ width: `${trackWidth}px` }}
    >
      <div className="track-label">Timeline</div>
      <div className="track-content">
        {/* Beat drop zones */}
        {Array.from({ length: musicalBeats }, (_, absoluteBeat) => (
          <BeatDropZone
            key={absoluteBeat}
            absoluteBeat={absoluteBeat}
            beatWidth={beatWidth}
          />
        ))}
        
        {/* General timeline drop zone as fallback */}
        <TimelineFallbackDropZone />
        
        {/* Beat grid lines */}
        <div className="beat-grid-lines">
          {Array.from({ length: totalVisualBeats - 1 }, (_, i) => (
            <div
              key={i}
              className="beat-grid-line"
              style={{
                position: 'absolute',
                left: `${(i + 1) * beatWidth}px`,
                width: '1px',
                height: '100%',
              }}
            />
          ))}
        </div>

        {/* Beat markers */}
        <div className="beat-markers">
          {Array.from({ length: totalVisualBeats }, (_, i) => {
            // Only show beat numbers within the dynamic range
            const isInDynamicRange = dynamicBeatRange.startBeat !== dynamicBeatRange.endBeat &&
                                   i >= dynamicBeatRange.startBeat &&
                                   i <= dynamicBeatRange.endBeat;
            const dynamicBeatNumber = i - dynamicBeatRange.startBeat;

            return (
              <div
                key={i}
                className={`beat-marker ${currentPlaybackBeat === i && isInDynamicRange ? 'active' : ''}`}
                style={{ left: `${i * beatWidth}px` }}
              >
                {isInDynamicRange ? dynamicBeatNumber : ''}
              </div>
            );
          })}
        </div>
        
        {/* All fragments positioned by their timing */}
        {fragments.map(fragment => {
          const state = fragmentStates.find(s => s.id === fragment.id);
          if (!state || state.currentBeat === null) return null;
          
          const leftPosition = state.currentBeat * beatWidth;
          
          // Calculate this fragment's time range
          const currentFragmentDuration = fragment.beatDuration;
          const currentStartTime = state.currentBeat;
          const currentEndTime = currentStartTime + currentFragmentDuration;
          
          // Find all fragments that overlap in time with this fragment
          const overlappingFragments = fragments.filter(f => {
            const fState = fragmentStates.find(s => s.id === f.id);
            if (!fState || fState.currentBeat === null) return false;
            
            const fDuration = f.beatDuration;
            const fStartTime = fState.currentBeat;
            const fEndTime = fStartTime + fDuration;
            
            // Check if time ranges overlap (including touching at endpoints)
            return fEndTime > currentStartTime && fStartTime < currentEndTime;
          });
          
          // Sort overlapping fragments by their start time, then by ID for consistency
          overlappingFragments.sort((a, b) => {
            const aState = fragmentStates.find(s => s.id === a.id)!;
            const bState = fragmentStates.find(s => s.id === b.id)!;
            const aStartTime = aState.currentBeat!;
            const bStartTime = bState.currentBeat!;
            
            if (aStartTime !== bStartTime) {
              return aStartTime - bStartTime;
            }
            return a.id.localeCompare(b.id);
          });
          
          
          const stackIndex = overlappingFragments.findIndex(f => f.id === fragment.id);
          const topPosition = 25 + (stackIndex * 85); // Stack fragments vertically with 85px spacing
          
          
          return (
            <div
              key={fragment.id}
              className="fragment-container"
              style={{ 
                position: 'absolute', 
                left: `${leftPosition}px`, 
                top: `${topPosition}px`,
                zIndex: 10 + stackIndex, // Ensure proper layering
              }}
            >
              <TimelineFragment
                fragment={fragment}
                state={state}
                beatWidth={beatWidth}
                isDragging={activeId === fragment.id}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
};

// Victory conditions: fragment_1@beat0, fragment_3@beat2, fragment_2@beat4, fragment_4@beat6
// Victory conditions are now derived from level configuration

// Solution hints calculations
const checkSolutionHints = (fragmentStates: FragmentState[], level: LevelConfig): HintState[] => {
  // Create position map from fragment states with relative positioning
  const placedFragments = fragmentStates.filter(state => state.currentBeat !== null);

  if (placedFragments.length === 0) {
    return level.solutionHints.map(() => 'inactive' as HintState);
  }

  // Find the leftmost placed fragment position
  const leftmostPlacedBeat = Math.min(...placedFragments.map(state => state.currentBeat!));

  // Create position map with relative positions
  const relativePositions = new Map<string, number>();
  fragmentStates.forEach(state => {
    if (state.currentBeat !== null) {
      relativePositions.set(state.id, state.currentBeat - leftmostPlacedBeat);
    }
  });
  
  // Check each hint and get its state
  const hintStates = level.solutionHints.map(hint => hint.check(relativePositions));
  
  return hintStates;
};

// Check victory condition
const checkVictoryCondition = (fragmentStates: FragmentState[], level: LevelConfig) => {
  // Victory condition: all fragments must be placed at their correct relative positions
  const placedFragments = fragmentStates.filter(state => state.currentBeat !== null);

  // Need all fragments to be placed
  if (placedFragments.length !== level.fragments.length) {
    return false;
  }

  // Find the leftmost placed fragment position
  const leftmostPlacedBeat = Math.min(...placedFragments.map(state => state.currentBeat!));

  // Find the leftmost correct position in the level (this is our reference point)
  const leftmostCorrectBeat = Math.min(...level.fragments.map(fragment => fragment.correctPosition.beat));

  // Check if each fragment is at the correct relative position from the leftmost
  return level.fragments.every(fragment => {
    const state = fragmentStates.find(s => s.id === fragment.id);
    if (!state || state.currentBeat === null) {
      return false;
    }

    // Calculate the expected relative position from the leftmost correct position
    const expectedRelativePosition = fragment.correctPosition.beat - leftmostCorrectBeat;
    const actualRelativePosition = state.currentBeat - leftmostPlacedBeat;

    return actualRelativePosition === expectedRelativePosition;
  });
};

// Solution Hints Component
const SolutionHints: React.FC<{
  fragmentStates: FragmentState[];
  hasPlayedThrough: boolean;
  level: LevelConfig;
}> = ({ fragmentStates, hasPlayedThrough, level }) => {
  const hintStates = hasPlayedThrough 
    ? checkSolutionHints(fragmentStates, level)
    : level.solutionHints.map(() => 'inactive' as HintState);

  return (
    <div className="solution-hints">
      <div className="hints-grid">
        {level.solutionHints.map((hint, index) => {
          const state = hintStates[index];
          // Get fragment names, extracting the number from the ID
          const frag1Num = hint.fragmentId1.split('_').pop();
          const frag2Num = hint.fragmentId2.split('_').pop();
          const label = `${frag1Num}-${frag2Num}`;
          
          return (
            <div
              key={index}
              className={`hint ${state}`}
              title={`${hint.fragment1Name} - ${hint.fragment2Name}`}
            >
              <div className="hint-light"></div>
              <div className="hint-label">{label}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// Victory Celebration Component
const VictoryCelebration: React.FC<{
  isVisible: boolean;
  levelName: string;
  onBackToLevels?: () => void;
  onNextLevel?: () => void;
  onStopAudio?: () => void;
}> = ({ isVisible, levelName, onBackToLevels, onNextLevel, onStopAudio }) => {
  if (!isVisible) return null;

  return (
    <div className="victory-celebration">
      <div className="victory-content">
        <div className="victory-title">🎼 Ancient Music Unlocked! 🎼</div>
        <div className="victory-subtitle">{levelName} Complete</div>
        <div className="victory-message">
          The polyphonic puzzle has been solved. The ancient rhythms flow in perfect harmony once more.
        </div>
        
        <div className="victory-actions">
          {onNextLevel && (
            <button 
              className="victory-btn victory-btn-primary"
              onClick={() => {
                onStopAudio?.();
                onNextLevel();
              }}
            >
              Next Excavation →
            </button>
          )}
          {onBackToLevels && (
            <button 
              className="victory-btn victory-btn-secondary"
              onClick={() => {
                onStopAudio?.();
                onBackToLevels();
              }}
            >
              ← Back to Sites
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

const BachPuzzle: React.FC<BachPuzzleProps> = ({ level = bachInvention779Level, onSolved, onBackToLevels, onNextLevel }) => {
  const [isMuted, setIsMuted] = useState(false);
  const [fragmentStates, setFragmentStates] = useState<FragmentState[]>(
    level.fragments.map((fragment) => ({
      id: fragment.id,
      currentBeat: null, // Start unplaced
      pitchOffset: 0,
      isActive: true // Keep for audio playback logic
    }))
  );
  const [activeId, setActiveId] = useState<string | null>(null);
  const [isVictorious, setIsVictorious] = useState(false);
  const [hasPlayedThrough, setHasPlayedThrough] = useState(false);
  const [currentArrangement, setCurrentArrangement] = useState<FragmentState[]>([]);
  const [currentPlaybackBeat, setCurrentPlaybackBeat] = useState<number>(-1);
  
  const synthsRef = useRef<{ [key: string]: Tone.Synth }>({});
  const partsRef = useRef<{ [key: string]: Tone.Part }>({});
  const beatTrackingPartRef = useRef<Tone.Part | null>(null);

  // Calculate dynamic beat range based on placed fragments
  const getDynamicBeatRange = (): { startBeat: number; endBeat: number } => {
    const placedFragments = fragmentStates.filter(state => state.currentBeat !== null);

    if (placedFragments.length === 0) {
      return { startBeat: 0, endBeat: 0 };
    }

    let minBeat = Infinity;
    let maxBeat = -Infinity;

    placedFragments.forEach(state => {
      const fragment = level.fragments.find(f => f.id === state.id);
      if (fragment && state.currentBeat !== null) {
        const startBeat = state.currentBeat;
        const endBeat = startBeat + fragment.beatDuration;

        minBeat = Math.min(minBeat, startBeat);
        maxBeat = Math.max(maxBeat, endBeat);
      }
    });

    return {
      startBeat: minBeat === Infinity ? 0 : minBeat,
      endBeat: maxBeat === -Infinity ? 0 : Math.ceil(maxBeat)
    };
  };

  // Single entry point for all fragment state changes
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleFragmentStateChange = useCallback(async (newFragmentStates: FragmentState[]) => {
    // Always cleanup first
    cleanupAudio();

    // Update fragment state
    setFragmentStates(newFragmentStates);

    // Start audio if any fragments are placed
    if (newFragmentStates.some(state => state.currentBeat !== null)) {
      await createAndStartLoop(newFragmentStates);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  
  // Reset component state when level changes
  useEffect(() => {
    // Reset state for new level using the unified handler
    const newFragmentStates = level.fragments.map((fragment) => ({
      id: fragment.id,
      currentBeat: null,
      pitchOffset: 0,
      isActive: true
    }));
    
    handleFragmentStateChange(newFragmentStates);

    // Reset other UI state
    setActiveId(null);
    setIsVictorious(false);
    setCurrentArrangement([]);
  }, [level.id, level.fragments, handleFragmentStateChange]); // Reset when level changes
  
  // Cleanup audio when component unmounts (e.g., navigating away)
  useEffect(() => {
    return () => {
      cleanupAudio();
    };
  }, []); // Empty dependency array - only runs on unmount
  
  // Create light X-axis snap modifier aligned with beat positions
  // Adjust beat width based on time signature - eighth notes need less space than quarter notes
  const getBeatWidth = (timeSignature: [number, number]): number => {
    const denominator = timeSignature[1];
    if (denominator === 8) {
      return 50;  // 50px for eighth note beats
    } else if (denominator === 16) {
      return 25;  // 25px for sixteenth note beats
    }
    return 100;  // 100px for quarter note beats (default)
  };
  
  const beatWidth = getBeatWidth(level.timeSignature);
  const maxFragmentDuration = getMaxFragmentDuration(level.fragments);
  const musicalBeats = level.totalBeats;
  // We need musical beats + max fragment duration to show fragments completely when placed at the last beat
  const totalVisualBeats = Math.ceil(musicalBeats + maxFragmentDuration);
  const lightXAxisSnapModifier = createLightXAxisSnapModifier(beatWidth, level.totalBeats);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor)
  );


  // Single cleanup function for all audio resources and state
  const cleanupAudio = () => {
    // Stop transport and clear scheduled events
    Tone.Transport.stop();
    Tone.Transport.cancel();

    // Remove transport event listeners
    Tone.Transport.off('start');
    Tone.Transport.off('loop');

    // Dispose beat tracking part
    if (beatTrackingPartRef.current) {
      beatTrackingPartRef.current.dispose();
      beatTrackingPartRef.current = null;
    }

    // Dispose all synths and parts
    Object.values(synthsRef.current).forEach(synth => {
      synth.triggerRelease();
      synth.dispose();
    });
    Object.values(partsRef.current).forEach(part => part.dispose());
    synthsRef.current = {};
    partsRef.current = {};

    // Reset playback state
    setCurrentArrangement([]);
    setCurrentPlaybackBeat(-1);
    setHasPlayedThrough(false);
  };

  const handleReset = () => {
    // Stop any playing audio
    cleanupAudio();

    // Reset all fragments to the available area
    setFragmentStates(
      level.fragments.map((fragment) => ({
        id: fragment.id,
        currentBeat: null,
        pitchOffset: 0,
        isActive: true
      }))
    );
  };

  // Get the beat unit notation based on time signature denominator
  const getBeatUnit = (timeSignature: [number, number]): string => {
    const denominator = timeSignature[1];
    switch (denominator) {
      case 2: return "2n";  // Half note
      case 4: return "4n";  // Quarter note (most common)
      case 8: return "8n";  // Eighth note
      case 16: return "16n"; // Sixteenth note
      default: return "4n";  // Default to quarter note
    }
  };

  // Convert beat number to Tone.js time notation based on time signature
  const beatToToneTime = (beat: number, timeSignature: [number, number]): string => {
    const denominator = timeSignature[1];
    if (denominator === 8) {
      // For x/8 time, beats are eighth notes
      // Convert to bars:quarters:sixteenths format
      const bars = Math.floor(beat / timeSignature[0]);
      const beatsInBar = beat % timeSignature[0];
      // Each eighth note = 0.5 quarter notes
      const quarters = Math.floor(beatsInBar / 2);
      const sixteenths = (beatsInBar % 2) * 2;
      return `${bars}:${quarters}:${sixteenths}`;
    } else if (denominator === 4) {
      // For x/4 time, beats are quarter notes
      const bars = Math.floor(beat / timeSignature[0]);
      const quarters = beat % timeSignature[0];
      return `${bars}:${quarters}:0`;
    }
    // Default fallback
    return `0:${beat}:0`;
  };

  const createAndStartLoop = async (currentFragmentStates: FragmentState[]) => {
    try {
      if (Tone.context.state !== 'running') {
        await Tone.start();
      }

      // Create synths for all fragments as needed
      level.fragments.forEach(fragment => {
        synthsRef.current[fragment.id] = new Tone.Synth().toDestination();
      });

    // Set time signature
    Tone.Transport.timeSignature = level.timeSignature;
    
    // Save the current arrangement at the start of playback
    setCurrentArrangement([...currentFragmentStates]);
    
    // Calculate dynamic beat range for this arrangement
    const getDynamicRangeForStates = (states: FragmentState[]) => {
      const placedFragments = states.filter(state => state.currentBeat !== null);

      if (placedFragments.length === 0) {
        return { startBeat: 0, endBeat: 0 };
      }

      let minBeat = Infinity;
      let maxBeat = -Infinity;

      placedFragments.forEach(state => {
        const fragment = level.fragments.find(f => f.id === state.id);
        if (fragment && state.currentBeat !== null) {
          const startBeat = state.currentBeat;
          const endBeat = startBeat + fragment.beatDuration;

          minBeat = Math.min(minBeat, startBeat);
          maxBeat = Math.max(maxBeat, endBeat);
        }
      });

      return {
        startBeat: minBeat === Infinity ? 0 : minBeat,
        endBeat: maxBeat === -Infinity ? 0 : Math.ceil(maxBeat)
      };
    };

    const dynamicRange = getDynamicRangeForStates(currentFragmentStates);
    
    // If no fragments are placed, do not play the loop
    if (dynamicRange.startBeat === dynamicRange.endBeat) {
      setCurrentPlaybackBeat(-1);
      setHasPlayedThrough(false);
      return;
    }
    
    // Calculate dynamic loop duration based on the beat range
    const dynamicBeats = dynamicRange.endBeat - dynamicRange.startBeat;
    const beatsPerMeasure = level.timeSignature[0]; // Use actual time signature
    const measuresNeeded = Math.ceil(dynamicBeats / beatsPerMeasure); // Round up to whole measures
    const loopDurationInBeats = measuresNeeded * beatsPerMeasure;

    // Configure Transport for looping
    Tone.Transport.bpm.value = level.tempo;
    Tone.Transport.loop = true;
    Tone.Transport.loopStart = "0";
    Tone.Transport.loopEnd = measuresNeeded + "m"; // m = measures

    // Create precise beat events for visual tracking
    const beatEvents = [];
    for (let i = 0; i < loopDurationInBeats; i++) {
      beatEvents.push({
        time: beatToToneTime(i, level.timeSignature), // Use correct time notation for time signature
        beat: i
      });
    }

    // Create visual sync Part for beat tracking
    const beatTrackingPart = new Tone.Part((time, event) => {
      const actualBeat = dynamicRange.startBeat + event.beat;

      // Use Tone.Draw for synchronized visual updates
      Tone.Draw.schedule(() => {
        setCurrentPlaybackBeat(actualBeat);
      }, time);
    }, beatEvents);

    beatTrackingPart.loop = true;
    beatTrackingPart.loopEnd = `${measuresNeeded}m`;
    beatTrackingPart.start("0");

    // Store reference for cleanup
    beatTrackingPartRef.current = beatTrackingPart;

    // Listen for Transport loop event to detect completion
    const handleLoopEnd = () => {
      setHasPlayedThrough(true);
    };
    Tone.Transport.on('loop', handleLoopEnd);

    // Create Tone.Part for each active fragment with offset positioning
    level.fragments.forEach(fragment => {
      const state = currentFragmentStates.find(s => s.id === fragment.id);

      if (state && state.isActive && state.currentBeat !== null) {
        const synth = synthsRef.current[fragment.id];
        if (synth) {
          // Transform note data for Tone.Part - Part expects events with 'time' property
          const noteEvents = fragment.notes
            .filter(noteData => noteData.note !== 'rest')
            .map(noteData => ({
              time: noteData.time,  // This is the time within the Part pattern
              note: noteData.note,
              duration: noteData.duration
            }));

          // Calculate fragment start position relative to dynamic range
          const fragmentStartBeat = state.currentBeat - dynamicRange.startBeat;

          // Create Part and start it at the correct offset using Tone.js offset notation
          const part = new Tone.Part((time, event) => {
            synth.triggerAttackRelease(event.note, event.duration, time);
          }, noteEvents);

          // Use offset notation to position the part based on time signature
          const beatUnit = getBeatUnit(level.timeSignature);
          const startOffset = {[beatUnit]: fragmentStartBeat};

          part.start(startOffset); // Start at fragmentStartBeat in the correct beat units
//          part.loop = true;
//          part.loopEnd = "2m"
//          part.loopEnd = {"4n": fragmentStartBeat + 2};
//          part.loopEnd = `+${loopDurationInBeats}n`;

          // Store reference for cleanup
          partsRef.current[fragment.id] = part;
        } else {
          console.warn('No synth found for fragment:', fragment.id);
        }
      }
    });

    // Start Transport for unified audio and visual playback
    Tone.Transport.start();

    } catch (error) {
      console.error('Error in createAndStartLoop:', error);
      throw error; // Re-throw to be caught by caller
    }
  };


  const stopAudio = () => {
    cleanupAudio();
  };





  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(event.active.id as string);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over, delta } = event;
    
    setActiveId(null);
    
    if (over && active.id !== over.id) {
      const dropId = over.id as string;
      
      if (dropId === 'fragment-bank') {
        // Remove fragment from timeline (unplace it)
        const newFragmentStates = fragmentStates.map(state =>
          state.id === active.id
            ? { ...state, currentBeat: null }
            : state
        );
        handleFragmentStateChange(newFragmentStates);
      } else if (dropId.startsWith('beat-') || over.id === 'timeline-fallback-drop') {
        const fragment = level.fragments.find(f => f.id === active.id);
        const fragmentState = fragmentStates.find(s => s.id === active.id);
        
        // Always use position-based calculation to match where the fragment visually lands
        let originalX = 0;
        if (fragment && fragmentState && fragmentState.currentBeat !== null) {
          originalX = fragmentState.currentBeat * beatWidth;
        }
        
        const finalX = originalX === 0 ? delta.x : originalX + delta.x;
        const absoluteBeat = Math.max(0, Math.min(musicalBeats - 1, Math.round(finalX / beatWidth)));
        
        const newFragmentStates = fragmentStates.map(state =>
          state.id === active.id
            ? { ...state, currentBeat: absoluteBeat }
            : state
        );
        handleFragmentStateChange(newFragmentStates);
      }
    }
  };


  useEffect(() => {
    // Only check victory condition after music has played through
    if (hasPlayedThrough) {
      const solved = checkVictoryCondition(currentArrangement, level);
      const wasVictorious = isVictorious;
      setIsVictorious(solved);
      
      // Only call onSolved when victory state changes from false to true (first time solved)
      if (solved && !wasVictorious) {
        onSolved(true);
      }
    } else {
      setIsVictorious(false);
      // Don't call onSolved(false) here to avoid repeated calls
    }
  }, [hasPlayedThrough, currentArrangement, onSolved, level, isVictorious]);


  return (
    <div className="bach-puzzle-container">
      {onBackToLevels && (
        <NavigationHeader 
          levelName={level.name}
          onBackToLevels={onBackToLevels}
        />
      )}
      
      <DndContext
      sensors={sensors}
      collisionDetection={pointerWithin}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      modifiers={[lightXAxisSnapModifier]}
    >
      <div className="bach-puzzle">
        <div className="playback-controls">
          <button 
            className="btn-mute" 
            onClick={() => {
              Tone.Destination.mute = !Tone.Destination.mute;
              setIsMuted(!isMuted);
            }}
          >
            {isMuted ? '🔇 Unmute' : '🔊 Mute'}
          </button>

          <button
            className="btn-reset"
            onClick={handleReset}
            disabled={!fragmentStates.some(state => state.currentBeat !== null)}
          >
            🔄 Reset
          </button>
          
          <SolutionHints 
            fragmentStates={currentArrangement.length > 0 ? currentArrangement : fragmentStates} 
            hasPlayedThrough={hasPlayedThrough}
            level={level}
          />
        </div>

        <div className="timeline-container">
          
          <div className="timeline-tracks">
            <Timeline
              fragments={level.fragments}
              fragmentStates={fragmentStates}
              trackWidth={totalVisualBeats * beatWidth}
              beatWidth={beatWidth}
              totalVisualBeats={totalVisualBeats}
              musicalBeats={musicalBeats}
              dynamicBeatRange={getDynamicBeatRange()}
              activeId={activeId}
              currentPlaybackBeat={currentPlaybackBeat}
            />
          </div>
        </div>

        {/* Fragment Bank for unplaced fragments */}
        <FragmentBank
          fragments={level.fragments}
          fragmentStates={fragmentStates}
          beatWidth={beatWidth}
          activeId={activeId}
        />
        
        {/* Victory celebration overlay */}
        <VictoryCelebration 
          isVisible={isVictorious} 
          levelName={level.name}
          onBackToLevels={onBackToLevels}
          onNextLevel={onNextLevel}
          onStopAudio={stopAudio}
        />
      </div>
      
      <DragOverlay>
        {activeId ? (
          <div style={{ opacity: 0.8, transform: 'rotate(2deg)' }}>
            <TimelineFragment
              fragment={level.fragments.find(f => f.id === activeId)!}
              state={fragmentStates.find(s => s.id === activeId)!}
              beatWidth={beatWidth}
            />
          </div>
        ) : null}
      </DragOverlay>
    </DndContext>
    </div>
  );
};

export default BachPuzzle;